import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import { mfaSetup } from '../services/api';
import PasswordInput from './PasswordInput';
import CopyButton from './ui/CopyButton';
import logoActivus from '../assets/ActivusSoftware.svg';

/**
 * Connexion en une ou deux étapes (DO-207) :
 * - password : identifiant + mot de passe ;
 * - setup    : premier passage sous MFA — QR code à scanner dans Microsoft / Google
 *              Authenticator, puis premier code (qui active le MFA) ;
 * - code     : code à 6 chiffres, demandé sur un navigateur inconnu ou après un mot de
 *              passe erroné. « Faire confiance à ce navigateur » évite le code pendant
 *              N jours (cookie HttpOnly posé par le back).
 */
export default function LoginPage() {
  const { login, verifyMfa } = useAuth();
  const navigate = useNavigate();
  const [form, setForm] = useState({ username: '', password: '' });
  const [step, setStep] = useState('password');   // password | setup | code
  const [challenge, setChallenge] = useState(null); // { mfaToken, mfaTrustDays }
  const [setup, setSetup] = useState(null);         // { qrCode, secret, issuer, account }
  const [code, setCode] = useState('');
  const [trustBrowser, setTrustBrowser] = useState(true);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const restart = message => {
    setStep('password');
    setChallenge(null); setSetup(null); setCode('');
    setForm(f => ({ ...f, password: '' }));
    setError(message || '');
  };

  const handlePassword = async e => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      const data = await login(form.username, form.password);
      if (data.token) {
        navigate('/');
      } else if (data.mfaSetupRequired) {
        setChallenge(data);
        setSetup(await mfaSetup(data.mfaToken));
        setStep('setup');
      } else {
        setChallenge(data);
        setStep('code');
      }
    } catch (err) {
      // 403 = compte bloqué (DO-208) ou IP refusée (robot), message fourni par le back
      setError(err.response?.status === 403
        ? (err.response.data?.error || 'Compte bloqué')
        : err.response?.status === 401 ? 'Identifiants incorrects' : 'Connexion impossible');
    } finally {
      setLoading(false);
    }
  };

  const handleCode = async e => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      await verifyMfa(challenge.mfaToken, code, trustBrowser);
      navigate('/');
    } catch (err) {
      const message = err.response?.data?.error;
      // Vérification expirée (5 min) : retour au mot de passe
      if (err.response?.status === 401 && message && message.startsWith('Vérification expirée')) {
        restart(message);
      } else {
        setError(message || 'Vérification impossible');
        setCode('');
      }
    } finally {
      setLoading(false);
    }
  };

  const codeForm = (
    <form onSubmit={handleCode}>
      <input
        style={{ ...styles.input, ...styles.code }}
        placeholder="000000"
        value={code}
        onChange={e => setCode(e.target.value.replace(/[^\d]/g, '').slice(0, 6))}
        inputMode="numeric"
        autoComplete="one-time-code"
        aria-label="Code à 6 chiffres"
        autoFocus
        required
      />
      <label style={styles.trust}>
        <input type="checkbox" checked={trustBrowser} onChange={e => setTrustBrowser(e.target.checked)} />
        Faire confiance à ce navigateur pendant {challenge?.mfaTrustDays ?? 30} jours
      </label>
      {error && <p style={styles.error}>{error}</p>}
      <button style={styles.btn} type="submit" disabled={loading || code.length !== 6}>
        {loading ? 'Vérification...' : step === 'setup' ? 'Activer et se connecter' : 'Valider'}
      </button>
      <button type="button" style={styles.back} onClick={() => restart()}>← Changer d'utilisateur</button>
    </form>
  );

  return (
    <div style={styles.page}>
      <div style={{ ...styles.card, ...(step === 'setup' ? { width: '420px' } : {}) }}>
        <img src={logoActivus} alt="Activus Group" style={styles.logo} />
        <h1 style={styles.title}>License Monitor</h1>
        <p style={styles.subtitle}>Administration</p>

        {step === 'password' && (
          <form onSubmit={handlePassword}>
            <input
              style={styles.input}
              placeholder="Utilisateur"
              value={form.username}
              onChange={e => setForm({ ...form, username: e.target.value })}
              required
            />
            <PasswordInput
              style={{ ...styles.input, marginBottom: 0 }}
              wrapperStyle={{ marginBottom: '1rem' }}
              placeholder="Mot de passe"
              value={form.password}
              onChange={e => setForm({ ...form, password: e.target.value })}
              required
            />
            {error && <p style={styles.error}>{error}</p>}
            <button style={styles.btn} type="submit" disabled={loading}>
              {loading ? 'Connexion...' : 'Se connecter'}
            </button>
          </form>
        )}

        {step === 'code' && (
          <>
            <p style={styles.intro}>
              Saisissez le code à 6 chiffres affiché par votre application d'authentification
              (Microsoft Authenticator, Google Authenticator…).
            </p>
            {codeForm}
          </>
        )}

        {step === 'setup' && setup && (
          <>
            <p style={styles.intro}>
              <b>Activation de la double authentification.</b> Dans Microsoft Authenticator ou
              Google Authenticator, ajoutez un compte et scannez ce QR code :
            </p>
            <img src={setup.qrCode} alt="QR code d'enrôlement" style={styles.qr} />
            <div style={styles.secretLabel}>Ou saisissez la clé à la main :</div>
            <div style={styles.secretRow}>
              <code style={styles.secret}>{setup.secret.match(/.{1,4}/g).join(' ')}</code>
              <CopyButton value={setup.secret} title="Copier la clé" />
            </div>
            <p style={styles.intro}>Puis saisissez le premier code affiché :</p>
            {codeForm}
          </>
        )}
      </div>
    </div>
  );
}

// Charte Activus Group : bleu nuit #232853, fond clair, texte #444.
const styles = {
  page: { minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#f5f5f5' },
  card: { background: '#ffffff', padding: '2.5rem', borderRadius: '12px', width: '360px', maxWidth: 'calc(100vw - 2rem)', boxSizing: 'border-box', boxShadow: '0 12px 40px rgba(0,0,0,0.12)', border: '1px solid #e6e6e6' },
  logo: { display: 'block', width: '220px', maxWidth: '100%', margin: '0 auto 1.25rem' },
  title: { color: '#232853', margin: '0 0 4px', fontSize: '1.35rem', textAlign: 'center' },
  subtitle: { color: '#6b7280', textAlign: 'center', marginBottom: '2rem' },
  input: { width: '100%', padding: '0.75rem', marginBottom: '1rem', borderRadius: '8px', border: '1px solid #cdcdcd', background: '#f5f5f5', color: '#444444', fontSize: '0.95rem', boxSizing: 'border-box' },
  code: { textAlign: 'center', fontSize: '1.4rem', letterSpacing: '0.4em', fontFamily: 'monospace' },
  error: { color: '#c62828', fontSize: '0.875rem', marginBottom: '0.75rem' },
  btn: { width: '100%', padding: '0.75rem', background: '#232853', color: '#fff', border: 'none', borderRadius: '8px', fontSize: '1rem', cursor: 'pointer', fontWeight: 600 },
  back: { display: 'block', margin: '0.85rem auto 0', background: 'none', border: 'none', color: '#6376aa', cursor: 'pointer', fontSize: '0.85rem' },
  intro: { color: '#444444', fontSize: '0.88rem', lineHeight: 1.45, margin: '0 0 0.85rem' },
  trust: { display: 'flex', alignItems: 'center', gap: '0.5rem', color: '#6b7280', fontSize: '0.85rem', marginBottom: '1rem', cursor: 'pointer' },
  qr: { display: 'block', width: '200px', height: '200px', margin: '0 auto 0.85rem', imageRendering: 'pixelated' },
  secretLabel: { color: '#6b7280', fontSize: '0.8rem', marginBottom: '0.3rem' },
  secretRow: { display: 'flex', alignItems: 'center', gap: '0.4rem', marginBottom: '1rem' },
  secret: { flex: 1, background: '#f5f5f5', border: '1px solid #e0e0e0', borderRadius: '6px', padding: '0.45rem 0.6rem', fontSize: '0.85rem', color: '#232853', wordBreak: 'break-all' },
};
